// Var-call parsing and declaration detection helpers for normalization.
// Example token: var(--theme.accent).
import {
  isIdentifierChar,
  isIdentifierStart,
  skipWhitespace,
} from './chars'

export type ParsedVarCall = {
  raw: string
  end: number
}

// Parse one `var(--name)` / `var(--name.key)` call at exact index.
export function parseVarCallAt(line: string, startIndex: number): ParsedVarCall | null {
  // Reject positions that do not begin with `var(`.
  if (!line.startsWith('var(', startIndex)) {
    return null
  } else {
    // Continue parsing var token body.
  }

  let cursor = startIndex + 4
  cursor = skipWhitespace(line, cursor)
  // Reject invalid var calls without `--` custom property prefix.
  if (line[cursor] !== '-' || line[cursor + 1] !== '-') {
    return null
  } else {
    // Continue custom property name scanning.
  }

  cursor += 2
  // Reject custom property names with invalid starting character.
  if (!isIdentifierStart(line[cursor] ?? '')) {
    return null
  } else {
    // Continue identifier body scanning.
  }

  cursor += 1
  // Consume remaining custom property name segment.
  while (cursor < line.length && isIdentifierChar(line[cursor])) {
    cursor += 1
  }

  cursor = skipWhitespace(line, cursor)
  // Consume optional `[index]` segment after variable name.
  if (line[cursor] === '[') {
    cursor += 1
    cursor = skipWhitespace(line, cursor)
    // Reject bracket access without numeric index.
    if (!isDigit(line[cursor] ?? '')) {
      return null
    } else {
      // Continue index digit scanning.
    }

    cursor += 1
    // Consume index digits until non-digit character.
    while (cursor < line.length && isDigit(line[cursor])) {
      cursor += 1
    }

    cursor = skipWhitespace(line, cursor)
    // Reject bracket access without closing `]`.
    if (line[cursor] !== ']') {
      return null
    } else {
      // Continue parsing after bracket access.
      cursor += 1
      cursor = skipWhitespace(line, cursor)
    }
  } else {
    // Continue parsing when bracket access is absent.
  }

  // Consume optional dotted key access segment.
  if (line[cursor] === '.') {
    cursor += 1
    // Reject dotted access when key does not start correctly.
    if (!isIdentifierStart(line[cursor] ?? '')) {
      return null
    } else {
      // Continue key segment scanning.
    }

    cursor += 1
    // Consume dotted key segment characters.
    while (cursor < line.length && isIdentifierChar(line[cursor])) {
      cursor += 1
    }
  } else {
    // Keep parsing when dotted key is absent.
  }

  cursor = skipWhitespace(line, cursor)
  // Reject malformed var calls without closing `)`.
  if (line[cursor] !== ')') {
    return null
  } else {
    const end = cursor + 1
    // Return parsed var-call token.
    return {
      raw: line.slice(startIndex, end),
      end,
    }
  }
}

// Detect decimal digits used in array index parsing.
function isDigit(char: string): boolean {
  // Return true for characters between `0` and `9`.
  return char >= '0' && char <= '9'
}


// Detect whether full trimmed line is exactly one var-call statement.
export function isStandaloneVarCall(trimmed: string): boolean {
  const normalized = trimmed.endsWith(';') ? trimmed.slice(0, -1).trimEnd() : trimmed
  const parsed = parseVarCallAt(normalized, 0)
  // Return true only when parser consumed the whole line.
  return Boolean(parsed && parsed.end === normalized.length)
}
